import { useState } from "react";
import { useT } from "../../i18n/provider";
import { call } from "../../lib/api";
import type { DrillTarget } from "../../lib/drill/targets";
import { ABSENT, asDate, asDateTime, asRate } from "../../lib/format";
import { asMoney, asUnits } from "../../lib/money";
import { useOperator } from "../../lib/session";
import { assigneeWord } from "../../lib/staff";
import { useRead, type Read } from "../../lib/use-read";
import { useStaffDirectory } from "../../lib/use-staff";
import { FailureBlock } from "../failure";
import { Loading } from "../kit";
import { StatusWord } from "../status";
import { SummaryList } from "../summary";
import { Window } from "../window";
import { useDrill, type OpenPanel } from "./context";
import { useVocabulary } from "../../i18n/vocabulary";

/**
 * The drill panels — docs/24 §7.
 *
 * **A panel is a subset of its page, never a different account of it.** Where a
 * rule travels with a figure it travels in both: a merchant panel showing a
 * payable balance carries the same hold/KYB status the merchant PAGE does,
 * because a held balance shown as available is how an ops agent promises a payout
 * the settlement run will not make — and that is not a fact one surface may keep
 * to itself.
 *
 * §11.1 for a panel: an error, a pending read and a missing record are three
 * DIFFERENT SENTENCES, kept apart without a skeleton. Four facts in a narrow
 * window do not earn one; what the rule forbids is the ONE sentence doing both
 * jobs, and `Read<T>` has no state that means both.
 */
export function DrillPanels() {
  const { panels, close } = useDrill();
  return (
    <>
      {panels.map((panel) => (
        <DrillWindow key={panel.key} panel={panel} onClose={() => close(panel.key)} />
      ))}
    </>
  );
}

/**
 * Sized to the shape of what is inside, not to a house default: a ledger card
 * and a risk timeline are tables, a support ticket is a transcript, and a box
 * sized for four figures makes a reader scroll a conversation three lines at a
 * time.
 *
 * A MAP rather than a wide/narrow flag, because `ticketedEvent` needs a third
 * size and the flag could not express it: the panel now carries the record, the
 * sessions, the money split and a gate, which is the most of any panel here.
 * Two sizes made it the same height as a four-figure merchant card, so the
 * figure an operator opened it FOR started below the fold.
 */
const PANEL_SIZE: Readonly<Record<string, { width: number; height: number }>> = {
  ledgerAccount: { width: 860, height: 560 },
  fraudSubject: { width: 860, height: 560 },
  supportTicket: { width: 860, height: 560 },
  loyaltyAccount: { width: 860, height: 560 },
  ticketedEvent: { width: 860, height: 660 },
};
const DEFAULT_PANEL_SIZE = { width: 640, height: 460 } as const;

function DrillWindow({ panel, onClose }: { panel: OpenPanel; onClose: () => void }) {
  const vocab = useVocabulary();
  return (
    <Window
      plane="float"
      /* Every drill arrives DOCKED — a modal, with smoke, `aria-modal`, centred,
         the register behind inert — and its first button undocks. Failure mode of
         float-by-default: a centred box over a LIVE register looks like a modal
         and is not, which is a click that lands on the wrong row. Failure mode of
         modal-only: the question a reader actually has about a read is
         comparative, and a scrim exists to prevent exactly that. */
      dockable
      title={vocab.drillTitle(panel.kind)}
      onClose={onClose}
      width={(PANEL_SIZE[panel.kind] ?? DEFAULT_PANEL_SIZE).width}
      height={(PANEL_SIZE[panel.kind] ?? DEFAULT_PANEL_SIZE).height}
    >
      <PanelBody target={panel} />
    </Window>
  );
}

/**
 * §7.7: **the panel switch ends in `never`**, so a new drill kind without a panel
 * fails `tsc` rather than opening a blank window.
 */
function PanelBody({ target }: { target: DrillTarget }) {
  switch (target.kind) {
    case "merchant":
      return <MerchantPanel id={target.id} />;
    case "subOrder":
      return <SubOrderPanel id={target.id} merchantId={target.params.merchantId ?? ""} />;
    case "payoutBatch":
      return <PayoutBatchPanel id={target.id} />;
    case "ledgerAccount":
      return <LedgerAccountPanel id={target.id} />;
    case "product":
      return <ProductPanel slug={target.id} />;
    case "returnRequest":
      return <ReturnRequestPanel id={target.id} />;
    case "fraudSubject":
      // `drillsFor` refuses to build this target without a kind, so the
      // parameter is never empty here — the same guarantee `subOrder` has.
      return <FraudSubjectPanel kind={target.params.subjectKind ?? ""} id={target.id} />;
    case "supportTicket":
      return <SupportTicketPanel id={target.id} />;
    case "loyaltyAccount":
      return <LoyaltyAccountPanel id={target.id} />;
    case "ticketedEvent":
      return <TicketedEventPanel idOrSlug={target.id} />;
    default:
      return assertNever(target.kind);
  }
}

function assertNever(kind: never): never {
  throw new Error(`no drill panel for kind ${String(kind)}`);
}

/** The three sentences, in one place so no panel writes a fourth. */
function PanelState<T>({ read, children }: { read: Read<T>; children: (data: T) => React.ReactNode }) {
  if (read.status === "loading") return <Loading rows={4} />;
  if (read.status === "failed") return <FailureBlock failure={read.failure} />;
  return <>{children(read.data)}</>;
}

// ── merchant ────────────────────────────────────────────────────────────────

function MerchantPanel({ id }: { id: string }) {
  const { i18n, locale } = useT();
  const vocab = useVocabulary();
  const { read } = useRead((signal) => call("GET /v1/admin/merchants/:id", { path: { id }, signal }), [id]);
  return (
    <PanelState read={read}>
      {(dto) => (
        <>
          <SummaryList
            items={[
              { label: i18n._("Trade name"), value: dto.merchant.tradeName[locale] },
              { label: i18n._("Legal name"), value: dto.merchant.legalName },
              { label: i18n._("Status"), value: <StatusWord domain="merchant" code={dto.merchant.status} /> },
              // The KYB decision travels with the merchant everywhere it is
              // shown; a panel that omitted it would be a different account of
              // the same record.
              { label: i18n._("KYB"), value: <StatusWord domain="kyb" code={dto.kyb.outcome} /> },
              { label: i18n._("Registration number"), value: dto.merchant.crn },
              { label: i18n._("TIN"), value: dto.merchant.tin },
              { label: i18n._("VAT registered"), value: dto.merchant.vatRegistered ? i18n._("Yes") : i18n._("No") },
              { label: i18n._("Tier"), value: dto.merchant.tier },
              { label: i18n._("Submitted"), value: asDateTime(dto.merchant.submittedAt) },
              { label: i18n._("Decided"), value: asDateTime(dto.merchant.decidedAt) },
              {
                label: i18n._("Payout account"),
                // Masked at rest by the merchant module: the full number is never
                // stored, so there is nothing here a console could leak.
                value: dto.bank ? `${dto.bank.bankCode} ${dto.bank.accountMasked}` : i18n._("Not provided"),
                wide: true,
              },
              ...(dto.merchant.decisionReason
                ? [{ label: i18n._("Reason"), value: dto.merchant.decisionReason, wide: true }]
                : []),
            ]}
          />
          <section>
            <h3 className="mb-2 text-sm font-semibold">{i18n._("Verification checks")}</h3>
            <ul className="flex flex-col gap-1">
              {dto.checks.map((check) => (
                <li key={check.kind} className="flex items-center justify-between gap-2 text-sm">
                  <span>{vocab.kybCheck(check.kind)}</span>
                  <span className="flex items-center gap-3">
                    {check.reason && <span className="text-xs text-muted-foreground">{check.reason}</span>}
                    <StatusWord domain="kybCheck" code={check.status} />
                  </span>
                </li>
              ))}
            </ul>
          </section>
        </>
      )}
    </PanelState>
  );
}

// ── sub-order ───────────────────────────────────────────────────────────────

function SubOrderPanel({ id, merchantId }: { id: string; merchantId: string }) {
  const { i18n, locale } = useT();
  const { read } = useRead(
    (signal) =>
      call("GET /v1/merchant/orders/:subOrderId", {
        path: { subOrderId: id },
        // An ADMIN session must name the merchant; `drillsFor` refuses to build
        // this target without one, so the parameter is never empty here.
        query: { merchantId },
        signal,
      }),
    [id, merchantId],
  );
  return (
    <PanelState read={read}>
      {(dto) => (
        <>
          <SummaryList
            items={[
              { label: i18n._("Order"), value: dto.orderNumber },
              { label: i18n._("Order status"), value: <StatusWord domain="order" code={dto.orderStatus} /> },
              { label: i18n._("Sub-order status"), value: <StatusWord domain="subOrder" code={dto.status} /> },
              { label: i18n._("Placed"), value: asDateTime(dto.placedAt) },
              { label: i18n._("Total"), value: asUnits(dto.totals.totalUnits, locale) },
              { label: i18n._("Service levels"), value: dto.serviceLevels.join(", ") || "—" },
            ]}
          />
          <section>
            <h3 className="mb-2 text-sm font-semibold">{i18n._("Lines")}</h3>
            <ul className="flex flex-col gap-2">
              {dto.lines.map((line) => (
                <li key={line.id} className="flex items-start justify-between gap-3 text-sm">
                  <span className="min-w-0">
                    <span className="block truncate">{line.title[locale]}</span>
                    <span className="text-xs text-muted-foreground">
                      {line.sku} · ×{line.quantity}
                    </span>
                  </span>
                  <span className="tabular shrink-0">{asUnits(line.totalUnits, locale)}</span>
                </li>
              ))}
            </ul>
          </section>
        </>
      )}
    </PanelState>
  );
}

// ── payout batch ────────────────────────────────────────────────────────────

function PayoutBatchPanel({ id }: { id: string }) {
  const { i18n, locale } = useT();
  const { read } = useRead(
    (signal) => call("GET /v1/finance/payouts/:batchId", { path: { batchId: id }, signal }),
    [id],
  );
  return (
    <PanelState read={read}>
      {(dto) => (
        <>
          <SummaryList
            items={[
              { label: i18n._("Status"), value: <StatusWord domain="payoutBatch" code={dto.status} /> },
              { label: i18n._("Scheduled for"), value: asDate(dto.scheduledFor) },
              { label: i18n._("Total"), value: asUnits(dto.totalUnits, locale) },
              { label: i18n._("Transfers"), value: String(dto.payoutCount) },
              { label: i18n._("Approved"), value: asDateTime(dto.approvedAt) },
              { label: i18n._("Sent"), value: asDateTime(dto.sentAt) },
            ]}
          />
          <section>
            <h3 className="mb-2 text-sm font-semibold">{i18n._("Transfers")}</h3>
            <ul className="flex flex-col gap-1">
              {dto.payouts.map((payout) => (
                <li key={payout.id} className="flex items-center justify-between gap-3 text-sm">
                  <span className="truncate font-mono text-xs">{payout.merchantId}</span>
                  <span className="flex shrink-0 items-center gap-3">
                    {/* The rounding is stated, not folded into the amount: every
                        transfer is a whole number of tögrög and the difference is
                        posted to ROUNDING_DIFFERENCE (docs/22 §8). */}
                    {payout.roundingUnits !== "0" && (
                      <span className="tabular text-xs text-muted-foreground">
                        {i18n._("rounding")} {asUnits(payout.roundingUnits, locale)}
                      </span>
                    )}
                    <span className="tabular">{asUnits(payout.amountUnits, locale)}</span>
                    <StatusWord domain="payout" code={payout.status} />
                  </span>
                </li>
              ))}
            </ul>
          </section>
        </>
      )}
    </PanelState>
  );
}

// ── ledger account card ─────────────────────────────────────────────────────

function LedgerAccountPanel({ id }: { id: string }) {
  const { i18n, locale } = useT();
  const { read } = useRead(
    (signal) => call("GET /v1/finance/accounts/:id/card", { path: { id }, signal }),
    [id],
  );
  return (
    <PanelState read={read}>
      {(dto) => (
        <>
          <SummaryList
            items={[
              { label: i18n._("Opening"), value: asMoney(dto.opening, locale) },
              { label: i18n._("Closing"), value: asMoney(dto.closing, locale) },
              { label: i18n._("From"), value: asDate(dto.from) },
              { label: i18n._("To"), value: asDate(dto.to) },
            ]}
          />
          <section className="min-w-0">
            <h3 className="mb-2 text-sm font-semibold">{i18n._("Movements")}</h3>
            {/* A read, never an editable amount: §0 — one module owns
                `postDocument`, and a correction is a POSTED REVERSAL the console
                submits, never a value it changes in place. */}
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-left text-xs text-muted-foreground">
                    <th className="py-1 pr-3 font-normal">{i18n._("Date")}</th>
                    <th className="py-1 pr-3 font-normal">{i18n._("Reference")}</th>
                    <th className="py-1 pr-3 text-right font-normal">{i18n._("Amount")}</th>
                    <th className="py-1 text-right font-normal">{i18n._("Running")}</th>
                  </tr>
                </thead>
                <tbody>
                  {dto.rows.map((row) => (
                    <tr key={row.entry.entryId} className="border-b border-border">
                      <td className="py-1 pr-3 whitespace-nowrap">{asDate(row.entry.businessDate)}</td>
                      <td className="max-w-40 truncate py-1 pr-3">{row.entry.reference}</td>
                      <td className="tabular py-1 pr-3 text-right">
                        {row.entry.side === "debit" ? "" : "-"}
                        {asMoney(row.entry.amount, locale)}
                      </td>
                      <td className="tabular py-1 text-right">{asMoney(row.running, locale)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}
    </PanelState>
  );
}

// ── product ─────────────────────────────────────────────────────────────────

function ProductPanel({ slug }: { slug: string }) {
  const { i18n, locale } = useT();
  const { read } = useRead(
    (signal) => call("GET /v1/products/:slug", { path: { slug }, query: { locale }, signal }),
    [slug, locale],
  );
  return (
    <PanelState read={read}>
      {(dto) => (
        <>
          {/* §1.1's consequence: this is a preview of CUSTOMER-FACING content, so
              anything drawn to look like the storefront would have to carry the
              customer palette. It deliberately does not try — these are the
              moderator's facts about a listing, not a rendering of the listing. */}
          <SummaryList
            items={[
              { label: i18n._("Title"), value: dto.title[locale] },
              { label: i18n._("Slug"), value: dto.slug },
              // The public reader answers for an ACTIVE product only, and the
              // public DTO carries neither the status nor the vendor-ownership
              // flag — the register's own row does, and that is where they are
              // drawn. A panel inventing them would be §7.8's «a different
              // account of the same record».
              { label: i18n._("Brand"), value: dto.brand?.name[locale] ?? "—" },
              { label: i18n._("Category"), value: dto.categoryPath.at(-1)?.name[locale] ?? "—" },
              { label: i18n._("Variants"), value: String(dto.variants.length) },
              { label: i18n._("In stock"), value: dto.inStock ? i18n._("Yes") : i18n._("No") },
            ]}
          />
          <section>
            <h3 className="mb-2 text-sm font-semibold">{i18n._("Variants")}</h3>
            <ul className="flex flex-col gap-1">
              {dto.variants.map((variant) => (
                <li key={variant.id} className="flex items-center justify-between gap-3 text-sm">
                  <span className="min-w-0 truncate">{variant.sku}</span>
                  <span className="shrink-0 text-xs text-muted-foreground">
                    {i18n._("{count} offers", { count: variant.offers.length })}
                  </span>
                </li>
              ))}
            </ul>
          </section>
        </>
      )}
    </PanelState>
  );
}

// ── return request ──────────────────────────────────────────────────────────

function ReturnRequestPanel({ id }: { id: string }) {
  const { i18n, locale } = useT();
  const vocab = useVocabulary();
  const { read } = useRead(
    (signal) => call("GET /v1/admin/returns/:id", { path: { id }, signal }),
    [id],
  );
  return (
    <PanelState read={read}>
      {(dto) => (
        <>
          <SummaryList
            items={[
              { label: i18n._("Status"), value: <StatusWord domain="return" code={dto.status} /> },
              { label: i18n._("Kind"), value: vocab.returnKind(dto.kind) },
              { label: i18n._("Reason"), value: vocab.returnReason(dto.reason) },
              { label: i18n._("Opened"), value: asDateTime(dto.requestedAt) },
              { label: i18n._("Window closes"), value: asDateTime(dto.windowExpiresAt) },
              { label: i18n._("Estimated refund"), value: asUnits(dto.refundEstimateUnits, locale) },
            ]}
          />
          <section>
            <h3 className="mb-2 text-sm font-semibold">{i18n._("Lines")}</h3>
            <ul className="flex flex-col gap-1">
              {dto.lines.map((line) => (
                <li key={line.lineId} className="flex items-center justify-between gap-3 text-sm">
                  <span className="min-w-0 truncate">{line.lineId}</span>
                  <span className="tabular shrink-0">×{line.quantity}</span>
                </li>
              ))}
            </ul>
          </section>
        </>
      )}
    </PanelState>
  );
}

// ── the risk record behind a row ────────────────────────────────────────────

/**
 * Standing, signals, assessments and cases for one subject, in one read.
 *
 * §7.8: **a panel is a subset of its page, never a different account of it.**
 * The standing block here carries the same listing and the same open-case count
 * the risk register's own columns do — a subject shown as unlisted in a panel
 * while the register beside it says BLOCK is how an operator releases something
 * they should not have.
 */
function FraudSubjectPanel({ kind, id }: { kind: string; id: string }) {
  const { i18n } = useT();
  const vocab = useVocabulary();
  const { read } = useRead(
    (signal) => call("GET /v1/admin/fraud/subjects/:kind/:id", { path: { kind, id }, signal }),
    [kind, id],
  );
  return (
    <PanelState read={read}>
      {(dto) => (
        <>
          <SummaryList
            items={[
              { label: i18n._("Kind"), value: vocab.fraudSubjectKind(dto.standing.kind) },
              // The value is the record's address and is often a hash. `break-all`
              // rather than truncation: an operator comparing two device hashes
              // needs the whole string, and an ellipsis in the middle of one is
              // the panel refusing the question it was opened to answer.
              {
                label: i18n._("Value"),
                value: <span className="font-mono text-xs break-all">{dto.standing.value}</span>,
                wide: true,
              },
              {
                label: i18n._("Listed"),
                value:
                  dto.standing.listing === null ? (
                    i18n._("Not on a list")
                  ) : (
                    <StatusWord domain="fraudListing" code={dto.standing.listing} />
                  ),
              },
              {
                label: i18n._("Worst decision, last 30 days"),
                value: <StatusWord domain="fraudDecision" code={dto.standing.worstDecision} />,
              },
              { label: i18n._("Assessments, last 30 days"), value: String(dto.standing.recentAssessments) },
              { label: i18n._("Open cases"), value: String(dto.standing.openCases) },
              ...(dto.standing.reason
                ? [{ label: i18n._("Why it is listed"), value: dto.standing.reason, wide: true }]
                : []),
              ...(dto.standing.expiresAt
                ? [{ label: i18n._("Listing expires"), value: asDateTime(dto.standing.expiresAt) }]
                : []),
            ]}
          />

          <TimelineSection title={i18n._("Cases")} empty={i18n._("No case has been opened about this.")} rows={dto.cases}>
            {(row) => (
              <li key={row.id} className="flex items-start justify-between gap-3 text-sm">
                <span className="min-w-0">
                  <span className="block truncate">{vocab.fraudContext(row.context)}</span>
                  <span className="text-xs text-muted-foreground">
                    {asDateTime(row.openedAt)} · {i18n._("score")} {row.score}
                  </span>
                </span>
                <StatusWord domain="fraudCase" code={row.status} />
              </li>
            )}
          </TimelineSection>

          <TimelineSection
            title={i18n._("Assessments")}
            empty={i18n._("Nothing has been assessed against this subject.")}
            rows={dto.assessments}
          >
            {(row) => (
              <li key={row.id} className="flex items-start justify-between gap-3 text-sm">
                <span className="min-w-0">
                  <span className="block truncate">{vocab.fraudContext(row.context)}</span>
                  <span className="text-xs text-muted-foreground">
                    {asDateTime(row.createdAt)}
                    {/* The rules that fired, by name. A score with no rules
                        beside it is a number nobody can argue with, and
                        arguing with it is the operator's whole job here. */}
                    {row.matched.length > 0 && ` · ${row.matched.map((rule) => rule.key).join(", ")}`}
                  </span>
                </span>
                <span className="flex shrink-0 items-center gap-2">
                  <span className="tabular text-xs text-muted-foreground">{row.score}</span>
                  <StatusWord domain="fraudDecision" code={row.decision} />
                </span>
              </li>
            )}
          </TimelineSection>

          <TimelineSection
            title={i18n._("Signals")}
            empty={i18n._("Nothing has been reported about this subject.")}
            rows={dto.signals}
          >
            {(row) => (
              <li key={row.id} className="flex items-start justify-between gap-3 text-sm">
                <span className="min-w-0">
                  {/* The kind is a dotted machine string and stays one: it is
                      what a rule's `velocity` counter names, so translating it
                      would break the only link between this row and the rule
                      that reads it. */}
                  <span className="block truncate font-mono text-xs">{row.kind}</span>
                  <span className="text-xs text-muted-foreground">
                    {asDateTime(row.occurredAt)} · {row.source}
                  </span>
                </span>
                <span className="shrink-0 text-xs">{vocab.fraudSeverity(row.severity)}</span>
              </li>
            )}
          </TimelineSection>
        </>
      )}
    </PanelState>
  );
}

/**
 * A section of a timeline, with **its own empty sentence**.
 *
 * §11.1 one level down: «no signals» and «no cases» are different facts about
 * the same subject, and a panel that drew one grey «nothing here» under three
 * headings would be telling an operator less than it knows.
 */
function TimelineSection<T>({
  title,
  empty,
  rows,
  children,
}: {
  title: string;
  empty: string;
  rows: readonly T[];
  children: (row: T) => React.ReactNode;
}) {
  return (
    <section className="min-w-0">
      <h3 className="mb-2 text-sm font-semibold">{title}</h3>
      {rows.length === 0 ? (
        <p className="text-xs text-muted-foreground">{empty}</p>
      ) : (
        <ul className="flex flex-col gap-2">{rows.map((row) => children(row))}</ul>
      )}
    </section>
  );
}

// ── support ticket ──────────────────────────────────────────────────────────

/**
 * One ticket and its whole transcript, internal notes included.
 *
 * The panel is a READ. Every verb on a ticket — assign, reply, transition,
 * priority — is a write and therefore a dialog on the queue page (§7.8), which
 * is also what keeps two operators from answering one customer twice from two
 * stacked panels.
 */
function SupportTicketPanel({ id }: { id: string }) {
  const { i18n } = useT();
  const vocab = useVocabulary();
  const me = useOperator();
  // §7.8: a panel is a subset of its page, never a different account of it. The
  // queue names the assignee, so this must name the same person with the same
  // four words — it drew the raw uuid until the staff directory existed, which
  // is the one fact on this panel an operator could not act on.
  const directory = useStaffDirectory();
  const { read } = useRead(
    (signal) => call("GET /v1/admin/support/tickets/:id", { path: { id }, signal }),
    [id],
  );
  return (
    <PanelState read={read}>
      {(dto) => (
        <>
          <SummaryList
            items={[
              { label: i18n._("Number"), value: dto.number },
              { label: i18n._("Subject"), value: dto.subject, wide: true },
              { label: i18n._("Status"), value: <StatusWord domain="ticket" code={dto.status} /> },
              { label: i18n._("Priority"), value: <StatusWord domain="ticketPriority" code={dto.priority} /> },
              { label: i18n._("Came in by"), value: vocab.ticketSource(dto.source) },
              {
                label: i18n._("Category"),
                value: dto.category ? vocab.ticketCategoryKind(dto.category.kind) : ABSENT,
              },
              { label: i18n._("Opened"), value: asDateTime(dto.createdAt) },
              { label: i18n._("Resolved"), value: asDateTime(dto.resolvedAt) },
              {
                label: i18n._("First reply due"),
                value: asDateTime(dto.sla.firstResponseDueAt),
              },
              { label: i18n._("Resolution due"), value: asDateTime(dto.sla.resolutionDueAt) },
              {
                label: i18n._("Assigned to"),
                value: assigneeWord(dto.assigneeAccountId, me?.account.id ?? null, directory.staff, {
                  nobody: i18n._("Nobody has picked it up"),
                  you: i18n._("You"),
                  somebodyElse: i18n._("Somebody else"),
                  unnamed: (short) => i18n._("Unnamed ({short})", { short }),
                }),
                wide: true,
              },
              /**
               * A guest ticket's number is unverified contact by definition —
               * ADR-0013 says a verified phone IS an account, so verifying it
               * would have created one. Saying so here is the difference between
               * an agent who trusts the number on the screen and one who asks.
               */
              ...(dto.guestPhoneLast4
                ? [
                    {
                      label: i18n._("Guest phone"),
                      value: (
                        <span>
                          ····{dto.guestPhoneLast4}{" "}
                          <span
                            className={
                              dto.guestContactVerifiedAt ? "text-xs text-success" : "text-xs text-caution"
                            }
                          >
                            {dto.guestContactVerifiedAt
                              ? i18n._("proven")
                              : i18n._("never proven — do not read anything private to it")}
                          </span>
                        </span>
                      ),
                      wide: true,
                    },
                  ]
                : []),
            ]}
          />

          {(dto.sla.firstResponseBreached || dto.sla.resolutionBreached) && (
            <p className="text-xs text-critical">
              {dto.sla.firstResponseBreached && dto.sla.resolutionBreached
                ? i18n._("Both the first-reply and the resolution promises were missed.")
                : dto.sla.firstResponseBreached
                  ? i18n._("The first-reply promise was missed.")
                  : i18n._("The resolution promise was missed.")}
            </p>
          )}

          <section className="min-w-0">
            <h3 className="mb-2 text-sm font-semibold">{i18n._("Conversation")}</h3>
            <ul className="flex flex-col gap-3">
              {dto.messages.map((message) => (
                <li key={message.id} className="flex min-w-0 flex-col gap-1">
                  <span className="flex items-center gap-2 text-xs text-muted-foreground">
                    <span className="font-semibold text-foreground">
                      {vocab.messageAuthor(message.authorKind)}
                    </span>
                    {asDateTime(message.createdAt)}
                    {/* An internal note is marked, always. An agent who cannot
                        tell a note from a reply is one edit away from telling a
                        customer what we said about them. */}
                    {message.internal && (
                      <span className="text-caution">{i18n._("internal note")}</span>
                    )}
                  </span>
                  <p className="text-sm whitespace-pre-wrap">{message.body}</p>
                </li>
              ))}
            </ul>
          </section>
        </>
      )}
    </PanelState>
  );
}

// ── loyalty account ─────────────────────────────────────────────────────────

/**
 * A buyer's points, the lots behind the balance, their wallet and their gift
 * cards.
 *
 * **The lots are the point of this panel.** A balance is one number and tells an
 * operator nothing about whether an adjustment is safe: points earned but not
 * yet confirmed cannot be spent, and points about to expire are the reason
 * somebody rang. §7.8 again — the balance travels with the lots that explain it.
 */
function LoyaltyAccountPanel({ id }: { id: string }) {
  const { i18n, locale } = useT();
  const vocab = useVocabulary();
  const { read } = useRead(
    (signal) => call("GET /v1/admin/loyalty/accounts/:id", { path: { id }, signal }),
    [id],
  );
  return (
    <PanelState read={read}>
      {(dto) => (
        <>
          <SummaryList
            items={[
              // `tier` is nullable: an account that has never ordered is on no
              // tier at all, which is a different fact from being on the entry
              // one and is what an operator about to adjust points is checking.
              { label: i18n._("Tier"), value: dto.tier?.name[locale] ?? i18n._("No tier yet") },
              { label: i18n._("Spendable points"), value: String(dto.balance.availablePoints) },
              { label: i18n._("Pending points"), value: String(dto.balance.pendingPoints) },
              // Held by a checkout in flight. Spendable + held is what the
              // ledger thinks exists, so an operator seeing only the first
              // number would read a mid-checkout account as short of points.
              { label: i18n._("Held by a checkout"), value: String(dto.balance.heldPoints) },
              { label: i18n._("Worth"), value: asUnits(dto.balance.valueUnits, locale) },
              { label: i18n._("Wallet"), value: asUnits(dto.walletBalanceUnits, locale) },
              { label: i18n._("Lifetime points"), value: String(dto.lifetimePoints) },
            ]}
          />

          {dto.expiringSoon.length > 0 && (
            <p className="text-xs text-caution">
              {i18n._("{points} points expire on {date}.", {
                points: dto.expiringSoon[0]!.points,
                date: asDate(dto.expiringSoon[0]!.expiresAt),
              })}
            </p>
          )}

          <TimelineSection
            title={i18n._("The lots behind the balance")}
            empty={i18n._("This account has never earned points.")}
            rows={dto.lots}
          >
            {(lot) => (
              <li key={lot.id} className="flex items-start justify-between gap-3 text-sm">
                <span className="min-w-0">
                  <span className="block truncate">
                    {/* Remaining, not granted: a lot half spent is a different
                        fact from a lot untouched, and the balance is the sum of
                        the first. */}
                    {i18n._("{remaining} of {points} left", {
                      remaining: lot.remainingPoints,
                      points: lot.points,
                    })}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {asDate(lot.earnedAt)} · {vocab.fundingSource(lot.fundingSource)}
                    {lot.expiresAt && ` · ${i18n._("expires")} ${asDate(lot.expiresAt)}`}
                  </span>
                </span>
                <StatusWord domain="pointLot" code={lot.status} />
              </li>
            )}
          </TimelineSection>

          <TimelineSection
            title={i18n._("Gift cards")}
            empty={i18n._("No gift card is attached to this account.")}
            rows={dto.giftCards}
          >
            {(card) => (
              <li key={card.id} className="flex items-center justify-between gap-3 text-sm">
                {/* The last four characters and nothing else — the code is shown
                    once, at issuance, and a console that could read it back would
                    be a console that can spend somebody's card. */}
                <span className="font-mono text-xs">····{card.codeLast4}</span>
                <span className="flex shrink-0 items-center gap-3">
                  <span className="tabular">{asUnits(card.balanceUnits, locale)}</span>
                  <StatusWord domain="giftCard" code={card.status} />
                </span>
              </li>
            )}
          </TimelineSection>
        </>
      )}
    </PanelState>
  );
}

// ── ticketed event ──────────────────────────────────────────────────────────

/**
 * An event and its sessions.
 *
 * Two reads, deliberately: the public event and the supervisory sales split.
 *
 * The second is `GET /v1/admin/events/:eventId/sales`, `tickets.read` — the
 * permission Wave 7 added precisely because this panel had a hole in it. The
 * arithmetic is the ORGANISER's own, unchanged: face value is the venue's,
 * the booking fee is the only part that is our revenue, and the venue's net is
 * the difference. An operator and a promoter disputing a payout must be reading
 * one number, so the api computes it once and both surfaces show that.
 *
 * **The two reads are kept apart rather than merged into one state.** The event
 * is the record; the sales are a figure about it. A sales read that fails must
 * not blank the panel — an operator answering «is this event even published»
 * still gets their answer — so the money band draws its own failure line and
 * the rest of the panel stands.
 *
 * The gate scans are a THIRD read and are per session, so they are fetched only
 * for the session an operator opens. Loading all of them would be one request
 * per session on a festival with thirty of them, to fill a section nobody has
 * asked to see.
 */
function TicketedEventPanel({ idOrSlug }: { idOrSlug: string }) {
  const { i18n, locale } = useT();
  const vocab = useVocabulary();
  const { read } = useRead(
    (signal) => call("GET /v1/events/:idOrSlug", { path: { idOrSlug }, signal }),
    [idOrSlug],
  );
  // The event may have been opened by SLUG (the reader takes either), and the
  // sales route takes an id — so the id comes off the record rather than out of
  // the drill target, and the second read waits for the first.
  const eventId = read.status === "ready" ? read.data.id : null;
  return (
    <PanelState read={read}>
      {(dto) => (
        <>
          <SummaryList
            items={[
              { label: i18n._("Title"), value: dto.title[locale], wide: true },
              { label: i18n._("Status"), value: <StatusWord domain="event" code={dto.status} /> },
              { label: i18n._("Venue"), value: dto.venue.name[locale] },
              { label: i18n._("City"), value: dto.venue.city },
              /**
               * The organiser is the principal and we are its agent
               * (docs/22 §6.7), so the ebarimt for a ticket is issued against
               * THEIR registration. A published event with no TIN would mean
               * receipts routed to us for money that is not our revenue, which
               * is why publication is blocked on it — and why an operator
               * reading this panel is shown it rather than having to ask.
               */
              {
                label: i18n._("Organiser tax registration"),
                value:
                  dto.organiserTin ?? (
                    <span className="text-caution">{i18n._("Not registered — cannot be published")}</span>
                  ),
                wide: true,
              },
              /*
                `asRate` takes BASIS POINTS, which is what every other caller in
                this console passes it (`asRate(row.commissionBps)`,
                `asRate(rateBp)`). Dividing by 100 first turned 500 bps into
                «0.05%» — a booking fee understated a hundredfold, on the one
                figure docs/22 §6.7 calls our whole revenue on a ticket. Found by
                opening the panel beside the fixture's own 5%.
              */
              { label: i18n._("Booking fee kept"), value: asRate(dto.platformFeeBps) },
              { label: i18n._("Handing a ticket on"), value: vocab.transferPolicy(dto.transferPolicy) },
              {
                label: i18n._("Refunds"),
                value: dto.refundPolicy.refundable
                  ? i18n._("Until {hours} h before the session, keeping {rate}", {
                      hours: dto.refundPolicy.cutoffHoursBeforeStart,
                      // Basis points, for the reason above: 1000 bps is 10%, not 0.1%.
                      rate: asRate(dto.refundPolicy.feeBps),
                    })
                  : i18n._("Not refundable"),
                wide: true,
              },
            ]}
          />

          <TimelineSection
            title={i18n._("Sessions")}
            empty={i18n._("This event has no sessions.")}
            rows={dto.sessions}
          >
            {(session) => (
              <li key={session.id} className="flex items-start justify-between gap-3 text-sm">
                <span className="min-w-0">
                  <span className="block truncate">{asDateTime(session.startsAt)}</span>
                  <span className="tabular text-xs text-muted-foreground">
                    {i18n._("{sold} sold, {held} held, of {capacity}", {
                      sold: session.sold,
                      held: session.held,
                      capacity: session.capacity,
                    })}
                  </span>
                </span>
                <StatusWord domain="eventSession" code={session.status} />
              </li>
            )}
          </TimelineSection>

          {eventId !== null && <EventSalesSection eventId={eventId} />}
        </>
      )}
    </PanelState>
  );
}

/**
 * What the event has taken, per session, and what is owed to the venue.
 *
 * **Three money columns, and only one of them is ours.** docs/22 §6.7 makes the
 * organiser the principal and us its agent, so face value is money we collected
 * on somebody else's behalf and the booking fee is the whole of our revenue.
 * Drawing them as one figure would be the console restating a settlement rule
 * as a total nobody can reconcile — which is §8a's argument about a second
 * implementation, applied to an aggregate rather than to a format.
 *
 * The scans sit behind the session they belong to, one read at a time.
 */
function EventSalesSection({ eventId }: { eventId: string }) {
  const { i18n, locale } = useT();
  const [openSession, setOpenSession] = useState<string | null>(null);
  const { read, reload } = useRead(
    (signal) => call("GET /v1/admin/events/:eventId/sales", { path: { eventId }, signal }),
    [eventId],
  );

  return (
    <section className="min-w-0">
      <h3 className="mb-2 text-sm font-semibold">{i18n._("Sales")}</h3>
      {read.status === "loading" ? (
        <Loading rows={3} />
      ) : read.status === "failed" ? (
        // Its own failure line, not the panel's: the record above it is still
        // the answer to «is this event published», and blanking that to report a
        // figure would be the panel losing the question it was opened for.
        <FailureBlock failure={read.failure} onRetry={reload} />
      ) : read.data.sessions.length === 0 ? (
        <p className="text-xs text-muted-foreground">{i18n._("Nothing has been sold yet.")}</p>
      ) : (
        <>
          <ul className="flex flex-col gap-2">
            {read.data.sessions.map((session) => (
              <li key={session.sessionId} className="flex flex-col gap-1">
                <div className="flex items-start justify-between gap-3 text-sm">
                  <span className="min-w-0">
                    <span className="block truncate">{asDateTime(session.startsAt)}</span>
                    <span className="tabular text-xs text-muted-foreground">
                      {i18n._("{issued} issued, {refunded} refunded, {scanned} scanned, of {capacity}", {
                        issued: session.issued,
                        refunded: session.refunded,
                        scanned: session.scanned,
                        capacity: session.capacity,
                      })}
                    </span>
                  </span>
                  <span className="flex shrink-0 flex-col items-end text-xs">
                    <span className="tabular">{asMoney(session.faceValue, locale)}</span>
                    <span className="tabular text-muted-foreground">
                      {i18n._("our fee {fee}", { fee: asMoney(session.platformFee, locale) })}
                    </span>
                    <span className="tabular text-muted-foreground">
                      {i18n._("venue {net}", { net: asMoney(session.venueNet, locale) })}
                    </span>
                  </span>
                </div>
                <button
                  type="button"
                  className="self-start text-xs text-primary underline-offset-2 outline-none hover:underline"
                  aria-expanded={openSession === session.sessionId}
                  onClick={() =>
                    setOpenSession((current) => (current === session.sessionId ? null : session.sessionId))
                  }
                >
                  {openSession === session.sessionId
                    ? i18n._("Hide the gate")
                    : i18n._("Who came through the gate")}
                </button>
                {openSession === session.sessionId && (
                  <SessionScans eventId={eventId} sessionId={session.sessionId} />
                )}
              </li>
            ))}
          </ul>
          <div className="mt-3">
            <SummaryList
              items={[
                { label: i18n._("Face value the buyers paid"), value: asMoney(read.data.totals.faceValue, locale) },
                { label: i18n._("Booking fee we keep"), value: asMoney(read.data.totals.platformFee, locale) },
                { label: i18n._("Owed to the venue"), value: asMoney(read.data.totals.venueNet, locale) },
              ]}
            />
          </div>
        </>
      )}
    </section>
  );
}

/**
 * One session's gate — admissions by door, and the recent stream.
 *
 * **The duplicates are the reason this is on the screen.** A burst of them at
 * one door is what a shared QR looks like from our side, and it is the sort of
 * thing a promoter rings us about rather than noticing themselves. So the three
 * counters are drawn side by side and the duplicate one is coloured when it is
 * not zero, rather than being one line of a table nobody scans.
 */
function SessionScans({ eventId, sessionId }: { eventId: string; sessionId: string }) {
  const { i18n } = useT();
  const vocab = useVocabulary();
  const { read, reload } = useRead(
    (signal) =>
      call("GET /v1/admin/events/:eventId/sessions/:sessionId/scans", {
        path: { eventId, sessionId },
        signal,
      }),
    [eventId, sessionId],
  );

  return (
    <div className="mt-1 rounded-lg border border-border p-3">
      {read.status === "loading" ? (
        <Loading rows={2} />
      ) : read.status === "failed" ? (
        <FailureBlock failure={read.failure} onRetry={reload} />
      ) : (
        <>
          <div className="flex flex-wrap items-end gap-6">
            <span className="flex flex-col">
              <span className="tabular text-xl leading-none font-semibold">{read.data.admitted}</span>
              <span className="text-xs text-muted-foreground">{i18n._("admitted")}</span>
            </span>
            <span className="flex flex-col">
              <span
                className={
                  read.data.duplicates === 0
                    ? "tabular text-xl leading-none font-semibold"
                    : "tabular text-xl leading-none font-semibold text-caution"
                }
              >
                {read.data.duplicates}
              </span>
              {/* §2.4: colour plus a word, never colour alone. */}
              <span className="text-xs text-muted-foreground">
                {read.data.duplicates === 0
                  ? i18n._("no ticket shown twice")
                  : i18n._("shown twice — a shared QR looks like this")}
              </span>
            </span>
            <span className="flex flex-col">
              <span className="tabular text-xl leading-none font-semibold">{read.data.invalid}</span>
              <span className="text-xs text-muted-foreground">{i18n._("turned away")}</span>
            </span>
          </div>

          <ul className="mt-3 flex flex-col gap-1 text-xs">
            {read.data.byGate.map((gate) => (
              <li key={gate.gate ?? "unnamed"} className="flex items-center justify-between gap-3">
                {/* A gate with no name is a scanner nobody labelled, which is a
                    different fact from a door called "1" — and on a venue with
                    six doors it is the one worth chasing. */}
                <span>{gate.gate ?? i18n._("An unnamed door")}</span>
                <span className="tabular text-muted-foreground">{gate.admitted}</span>
              </li>
            ))}
          </ul>

          <TimelineSection
            title={i18n._("Recent scans")}
            empty={i18n._("Nobody has been scanned in yet.")}
            rows={read.data.recent}
          >
            {(scan) => (
              <li key={scan.scanId} className="flex items-center justify-between gap-3 text-xs">
                <span className="flex min-w-0 items-center gap-2">
                  <StatusWord domain="scanResult" code={scan.result} />
                  <span className="truncate text-muted-foreground">
                    {scan.gate ?? i18n._("An unnamed door")}
                  </span>
                </span>
                <span className="flex shrink-0 items-center gap-2 text-muted-foreground">
                  {/* An OFFLINE scan was decided on the scanner and reconciled
                      later, so a duplicate among them is the gate having no way
                      to know — a different conversation from one online. */}
                  <span>{vocab.scanMode(scan.mode)}</span>
                  <span className="tabular">{asDateTime(scan.at)}</span>
                </span>
              </li>
            )}
          </TimelineSection>
        </>
      )}
    </div>
  );
}

